import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import { Textarea } from '../components/ui/textarea';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '../components/ui/dialog';
import { toast } from 'sonner';
import {
  Tent, Plus, Trash2, CheckCircle2, XCircle, Clock, Edit, Save, RefreshCw,
  Building2, Mail, Phone, Star, Search, AlertCircle, DollarSign, Eye, Ban, Send, History, MapPin, ShieldAlert
} from 'lucide-react';

const API_URL = `${process.env.REACT_APP_BACKEND_URL}/api`;

const STATUS_CONFIG = {
  pending:         { label: 'Pending',          color: 'bg-amber-500/10 text-amber-500 border-amber-500/30',   icon: Clock,        dotColor: 'bg-amber-400' },
  under_review:    { label: 'Under Review',     color: 'bg-blue-500/10 text-blue-500 border-blue-500/30',      icon: Eye,          dotColor: 'bg-blue-400' },
  payment_pending: { label: 'Payment Pending',  color: 'bg-orange-500/10 text-orange-500 border-orange-500/30', icon: DollarSign,   dotColor: 'bg-orange-400' },
  confirmed:       { label: 'Confirmed',        color: 'bg-cameroon-green/10 text-cameroon-green border-cameroon-green/30', icon: CheckCircle2, dotColor: 'bg-cameroon-green' },
  info_needed:     { label: 'Info Needed',       color: 'bg-purple-500/10 text-purple-500 border-purple-500/30', icon: AlertCircle,  dotColor: 'bg-purple-400' },
  rejected:        { label: 'Rejected',         color: 'bg-red-500/10 text-red-500 border-red-500/30',         icon: Ban,          dotColor: 'bg-red-400' },
  cancelled:       { label: 'Cancelled',        color: 'bg-slate-500/10 text-slate-400 border-slate-500/30',   icon: XCircle,      dotColor: 'bg-slate-400' },
};

const STATUS_OPTIONS = [
  { value: 'under_review',    label: 'Under Review',    description: 'Mark as being reviewed' },
  { value: 'payment_pending', label: 'Payment Pending',  description: 'Approved, awaiting payment' },
  { value: 'confirmed',       label: 'Confirmed',       description: 'Payment received, confirmed' },
  { value: 'info_needed',     label: 'Info Needed',      description: 'Request additional information' },
  { value: 'rejected',        label: 'Rejected',        description: 'Decline this application' },
  { value: 'cancelled',       label: 'Cancelled',       description: 'Cancel this reservation' },
];

const StatusBadge = ({ status }) => {
  const cfg = STATUS_CONFIG[status] || STATUS_CONFIG.pending;
  const Icon = cfg.icon;
  return (
    <Badge className={`${cfg.color} border gap-1`} data-testid={`status-badge-${status}`}>
      <Icon className="w-3 h-3" />
      {cfg.label}
    </Badge>
  );
};

// Status update dialog component
const StatusUpdateDialog = ({ open, onClose, onSubmit, currentStatus, itemType }) => {
  const [newStatus, setNewStatus] = useState('');
  const [adminNote, setAdminNote] = useState('');
  const [paymentRef, setPaymentRef] = useState('');
  const [tentNumber, setTentNumber] = useState('');
  const [submitting, setSubmitting] = useState(false);

  const handleSubmit = async () => {
    if (!newStatus) { toast.error('Please select a status'); return; }
    setSubmitting(true);
    try {
      await onSubmit(newStatus, adminNote, paymentRef, tentNumber);
      setNewStatus('');
      setAdminNote('');
      setPaymentRef('');
      setTentNumber('');
      onClose();
    } finally {
      setSubmitting(false);
    }
  };

  const showTentNumber = newStatus === 'confirmed' && itemType === 'Reservation';

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-md" data-testid="status-update-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Send className="w-5 h-5 text-cameroon-green" />
            Update {itemType} Status
          </DialogTitle>
        </DialogHeader>
        <div className="space-y-4 mt-2">
          <div className="space-y-2">
            <Label>Current Status</Label>
            <StatusBadge status={currentStatus} />
          </div>
          <div className="space-y-2">
            <Label>New Status</Label>
            <Select value={newStatus} onValueChange={setNewStatus}>
              <SelectTrigger data-testid="status-select">
                <SelectValue placeholder="Select new status..." />
              </SelectTrigger>
              <SelectContent>
                {STATUS_OPTIONS.filter(o => o.value !== currentStatus).map(opt => (
                  <SelectItem key={opt.value} value={opt.value}>
                    <div className="flex items-center gap-2">
                      <div className={`w-2 h-2 rounded-full ${STATUS_CONFIG[opt.value]?.dotColor}`} />
                      <span>{opt.label}</span>
                      <span className="text-xs text-muted-foreground ml-1">- {opt.description}</span>
                    </div>
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          {showTentNumber && (
            <div className="space-y-2 p-3 rounded-lg bg-cameroon-green/5 border border-cameroon-green/20">
              <Label className="flex items-center gap-2">
                <MapPin className="w-4 h-4 text-cameroon-green" />
                Assign Tent / Spot Number
              </Label>
              <Input
                placeholder="e.g. T-12, Spot A5, Zone B Row 3"
                value={tentNumber}
                onChange={(e) => setTentNumber(e.target.value)}
                className="font-mono text-lg"
                data-testid="tent-number-input"
              />
              <p className="text-xs text-muted-foreground">This number will be emailed to the client so they know exactly where to set up their tent/canopy on event day.</p>
            </div>
          )}
          {newStatus === 'confirmed' && !showTentNumber && (
            <div className="space-y-2">
              <Label>Payment Reference (optional)</Label>
              <Input
                placeholder="e.g. Interac ref #12345"
                value={paymentRef}
                onChange={(e) => setPaymentRef(e.target.value)}
                data-testid="payment-ref-input"
              />
            </div>
          )}
          {newStatus === 'confirmed' && showTentNumber && (
            <div className="space-y-2">
              <Label>Payment Reference (optional)</Label>
              <Input
                placeholder="e.g. Interac ref #12345"
                value={paymentRef}
                onChange={(e) => setPaymentRef(e.target.value)}
                data-testid="payment-ref-input"
              />
            </div>
          )}
          <div className="space-y-2">
            <Label>Admin Note {newStatus === 'info_needed' ? '(required - explain what info is needed)' : '(optional - included in email)'}</Label>
            <Textarea
              placeholder={newStatus === 'info_needed' ? "Please provide your business license number and proof of insurance..." : "Add a note for the applicant..."}
              value={adminNote}
              onChange={(e) => setAdminNote(e.target.value)}
              rows={3}
              data-testid="admin-note-input"
            />
          </div>
          <div className="p-3 rounded-lg bg-muted/50 text-sm text-muted-foreground flex items-start gap-2">
            <Mail className="w-4 h-4 mt-0.5 flex-shrink-0" />
            <span>An email notification will be sent to the applicant with the new status{adminNote ? ' and your note' : ''}.</span>
          </div>
        </div>
        <DialogFooter className="mt-4">
          <Button variant="outline" onClick={onClose}>Cancel</Button>
          <Button
            onClick={handleSubmit}
            disabled={!newStatus || submitting || (newStatus === 'info_needed' && !adminNote.trim())}
            className="bg-cameroon-green hover:bg-cameroon-green/90"
            data-testid="submit-status-update"
          >
            {submitting ? 'Updating...' : 'Update & Send Email'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

// Status history timeline
const StatusHistory = ({ history }) => {
  if (!history || history.length === 0) return null;
  return (
    <div className="mt-3 pt-3 border-t border-dashed" data-testid="status-history">
      <p className="text-xs font-semibold text-muted-foreground mb-2 flex items-center gap-1">
        <History className="w-3 h-3" /> Status History
      </p>
      <div className="space-y-1">
        {history.slice().reverse().map((entry, idx) => {
          const cfg = STATUS_CONFIG[entry.status] || STATUS_CONFIG.pending;
          return (
            <div key={`${entry.changed_at}-${idx}`} className="flex items-start gap-2 text-xs">
              <div className={`w-2 h-2 rounded-full mt-1.5 flex-shrink-0 ${cfg.dotColor}`} />
              <div className="flex-1 min-w-0">
                <span className="font-medium">{cfg.label}</span>
                <span className="text-muted-foreground ml-1">
                  {new Date(entry.changed_at).toLocaleDateString()} {new Date(entry.changed_at).toLocaleTimeString([], {hour: '2-digit', minute:'2-digit'})}
                </span>
                {entry.changed_by && <span className="text-muted-foreground ml-1">by {entry.changed_by}</span>}
                {entry.note && <p className="text-muted-foreground italic mt-0.5 truncate">{entry.note}</p>}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
};

// Delete confirmation dialog with typed confirmation
const DeleteConfirmDialog = ({ open, onClose, onConfirm, itemName, confirmationNumber, itemType }) => {
  const [confirmText, setConfirmText] = useState('');
  const [deleting, setDeleting] = useState(false);
  const expectedText = 'DELETE';

  const handleConfirm = async () => {
    setDeleting(true);
    try {
      await onConfirm();
      setConfirmText('');
      onClose();
    } finally {
      setDeleting(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => { if (!o) { setConfirmText(''); onClose(); } }}>
      <DialogContent className="max-w-md" data-testid="delete-confirm-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 text-destructive">
            <ShieldAlert className="w-5 h-5" />
            Delete {itemType}
          </DialogTitle>
        </DialogHeader>
        <div className="space-y-4 mt-2">
          <div className="p-4 rounded-lg bg-destructive/10 border border-destructive/20">
            <p className="text-sm font-semibold text-destructive">This action is permanent and cannot be undone.</p>
            <p className="text-sm text-muted-foreground mt-2">
              You are about to permanently delete the following {itemType.toLowerCase()}:
            </p>
            <div className="mt-3 p-3 rounded bg-muted/50 space-y-1">
              <p className="font-semibold text-sm">{itemName}</p>
              <p className="text-xs font-mono text-muted-foreground">{confirmationNumber}</p>
            </div>
          </div>
          <div className="space-y-2">
            <Label>Type <span className="font-mono font-bold text-destructive">DELETE</span> to confirm</Label>
            <Input
              value={confirmText}
              onChange={(e) => setConfirmText(e.target.value)}
              placeholder="Type DELETE here..."
              className="font-mono"
              data-testid="delete-confirm-input"
              autoComplete="off"
            />
          </div>
        </div>
        <DialogFooter className="mt-4">
          <Button variant="outline" onClick={() => { setConfirmText(''); onClose(); }}>Cancel</Button>
          <Button
            variant="destructive"
            onClick={handleConfirm}
            disabled={confirmText !== expectedText || deleting}
            data-testid="delete-confirm-btn"
          >
            {deleting ? 'Deleting...' : 'Permanently Delete'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

const TentSponsorManagement = ({ eventId }) => {
  const [config, setConfig] = useState(null);
  const [reservations, setReservations] = useState([]);
  const [packages, setPackages] = useState([]);
  const [sponsors, setSponsors] = useState([]);
  const [loading, setLoading] = useState(true);
  const [newTentType, setNewTentType] = useState({ name: '', description: '', size: '', price: 0, total_spots: 10 });
  const [newPackage, setNewPackage] = useState({ name: '', price: 0, total_available: 5, perks: '', logo_size: 'medium', display_priority: 10 });
  const [showAddTent, setShowAddTent] = useState(false);
  const [showAddPkg, setShowAddPkg] = useState(false);
  const [editingConfig, setEditingConfig] = useState(false);
  const [configForm, setConfigForm] = useState({ instructions: '', payment_account_name: '', payment_account_type: 'host_association', payment_email: '', payment_phone: '', payment_instructions: '' });
  const [statusDialog, setStatusDialog] = useState({ open: false, id: null, currentStatus: '', type: 'reservation' });
  const [deleteDialog, setDeleteDialog] = useState({ open: false, id: null, name: '', confirmation: '', type: 'reservation' });
  const [filterStatus, setFilterStatus] = useState('all');

  const fetchAll = useCallback(async () => {
    setLoading(true);
    try {
      const [configRes, resRes, pkgRes, sponsorRes] = await Promise.all([
        axios.get(`${API_URL}/events/${eventId}/tent-config`),
        axios.get(`${API_URL}/events/${eventId}/tent-reservations`),
        axios.get(`${API_URL}/events/${eventId}/sponsorship-packages`),
        axios.get(`${API_URL}/events/${eventId}/sponsor-applications`)
      ]);
      setConfig(configRes.data);
      setConfigForm({
        instructions: configRes.data.instructions || '',
        payment_account_name: configRes.data.payment_account_name || '',
        payment_account_type: configRes.data.payment_account_type || 'host_association',
        payment_email: configRes.data.payment_email || '',
        payment_phone: configRes.data.payment_phone || '',
        payment_instructions: configRes.data.payment_instructions || ''
      });
      setReservations(resRes.data);
      setPackages(pkgRes.data);
      setSponsors(sponsorRes.data);
    } catch {
      toast.error('Failed to load data');
    } finally {
      setLoading(false);
    }
  }, [eventId]);

  useEffect(() => { if (eventId) fetchAll(); }, [eventId, fetchAll]);

  const handleSaveConfig = async () => {
    try {
      await axios.put(`${API_URL}/events/${eventId}/tent-config`, configForm);
      toast.success('Configuration saved');
      setEditingConfig(false);
      fetchAll();
    } catch { toast.error('Failed to save config'); }
  };

  const handleAddTentType = async () => {
    try {
      await axios.post(`${API_URL}/events/${eventId}/tent-types`, { ...newTentType, price: parseFloat(newTentType.price), total_spots: parseInt(newTentType.total_spots) });
      toast.success('Tent type added');
      setNewTentType({ name: '', description: '', size: '', price: 0, total_spots: 10 });
      setShowAddTent(false);
      fetchAll();
    } catch { toast.error('Failed to add tent type'); }
  };

  const handleDeleteTentType = async (typeId) => {
    if (!window.confirm('Delete this tent type?')) return;
    try {
      await axios.delete(`${API_URL}/events/${eventId}/tent-types/${typeId}`);
      toast.success('Tent type removed');
      fetchAll();
    } catch { toast.error('Failed to delete'); }
  };

  const handleAddPackage = async () => {
    try {
      const perks = newPackage.perks.split('\n').filter(p => p.trim());
      await axios.post(`${API_URL}/events/${eventId}/sponsorship-packages`, {
        ...newPackage, price: parseFloat(newPackage.price), total_available: parseInt(newPackage.total_available),
        display_priority: parseInt(newPackage.display_priority), perks
      });
      toast.success('Package created');
      setNewPackage({ name: '', price: 0, total_available: 5, perks: '', logo_size: 'medium', display_priority: 10 });
      setShowAddPkg(false);
      fetchAll();
    } catch { toast.error('Failed to create package'); }
  };

  const handleDeletePackage = async (pkgId) => {
    if (!window.confirm('Delete this package?')) return;
    try {
      await axios.delete(`${API_URL}/sponsorship-packages/${pkgId}`);
      toast.success('Package deleted');
      fetchAll();
    } catch { toast.error('Failed to delete'); }
  };

  const handleStatusUpdate = async (newStatus, adminNote, paymentRef, tentNumber) => {
    const { id, type } = statusDialog;
    const endpoint = type === 'reservation'
      ? `${API_URL}/tent-reservations/${id}/status`
      : `${API_URL}/sponsor-applications/${id}/status`;
    try {
      const body = { status: newStatus, admin_note: adminNote || '', payment_reference: paymentRef || '' };
      if (tentNumber) body.tent_number = tentNumber;
      await axios.put(endpoint, body);
      toast.success(`Status updated to ${STATUS_CONFIG[newStatus]?.label || newStatus}. Email sent.`);
      fetchAll();
    } catch {
      toast.error('Failed to update status');
      throw new Error('failed');
    }
  };

  const openStatusDialog = (id, currentStatus, type) => {
    setStatusDialog({ open: true, id, currentStatus, type });
  };

  const handleDelete = async () => {
    const { id, type } = deleteDialog;
    const endpoint = type === 'reservation'
      ? `${API_URL}/tent-reservations/${id}`
      : `${API_URL}/sponsor-applications/${id}`;
    try {
      await axios.delete(endpoint);
      toast.success(`${type === 'reservation' ? 'Reservation' : 'Sponsor application'} deleted permanently.`);
      fetchAll();
    } catch {
      toast.error('Failed to delete');
      throw new Error('failed');
    }
  };

  const openDeleteDialog = (id, name, confirmation, type) => {
    setDeleteDialog({ open: true, id, name, confirmation, type });
  };

  const filteredReservations = filterStatus === 'all' ? reservations : reservations.filter(r => r.status === filterStatus);
  const filteredSponsors = filterStatus === 'all' ? sponsors : sponsors.filter(s => s.status === filterStatus);

  const statusCounts = (items) => {
    const counts = {};
    items.forEach(i => { counts[i.status] = (counts[i.status] || 0) + 1; });
    return counts;
  };

  if (loading) return <div className="animate-pulse space-y-4">{Array(3).fill(0).map((_, i) => <div key={i} className="h-20 bg-muted rounded-lg" />)}</div>;

  const resCounts = statusCounts(reservations);
  const sposCounts = statusCounts(sponsors);

  return (
    <div className="space-y-6" data-testid="tent-sponsor-management">
      <div className="flex items-center justify-between">
        <h2 className="font-serif text-xl font-bold flex items-center gap-2"><Tent className="w-5 h-5 text-cameroon-green" /> Tent & Sponsorship</h2>
        <Button variant="outline" size="sm" onClick={fetchAll}><RefreshCw className="w-4 h-4 mr-1" />Refresh</Button>
      </div>

      <Tabs defaultValue="config">
        <TabsList className="grid grid-cols-4 w-full">
          <TabsTrigger value="config">Configuration</TabsTrigger>
          <TabsTrigger value="reservations">Reservations ({reservations.length})</TabsTrigger>
          <TabsTrigger value="packages">Packages ({packages.length})</TabsTrigger>
          <TabsTrigger value="sponsors">Sponsors ({sponsors.length})</TabsTrigger>
        </TabsList>

        {/* CONFIG TAB */}
        <TabsContent value="config" className="space-y-4">
          <Card>
            <CardHeader>
              <div className="flex justify-between items-center">
                <CardTitle className="text-base">Payment & Instructions</CardTitle>
                <Button size="sm" variant={editingConfig ? "default" : "outline"} onClick={() => editingConfig ? handleSaveConfig() : setEditingConfig(true)}>
                  {editingConfig ? <><Save className="w-4 h-4 mr-1" />Save</> : <><Edit className="w-4 h-4 mr-1" />Edit</>}
                </Button>
              </div>
            </CardHeader>
            <CardContent className="space-y-3">
              <div className="space-y-2">
                <Label>Reservation Instructions</Label>
                <Textarea disabled={!editingConfig} value={configForm.instructions} onChange={e => setConfigForm(p => ({...p, instructions: e.target.value}))} rows={2} />
              </div>
              <div className="space-y-2">
                <Label>e-Transfer Account Holder Name</Label>
                <Input disabled={!editingConfig} value={configForm.payment_account_name} onChange={e => setConfigForm(p => ({...p, payment_account_name: e.target.value}))} placeholder="e.g. Association Ngoketunjia de Montreal" data-testid="config-payment-account-name" />
              </div>
              <div className="space-y-2">
                <Label>Account Type</Label>
                <div className="flex gap-3">
                  <label className={`flex-1 flex items-center gap-2 p-3 rounded-lg border cursor-pointer transition-colors ${configForm.payment_account_type === 'host_association' ? 'border-cameroon-green bg-cameroon-green/5' : 'border-muted'} ${!editingConfig ? 'opacity-60 pointer-events-none' : ''}`}>
                    <input type="radio" name="account_type" value="host_association" checked={configForm.payment_account_type === 'host_association'} onChange={() => setConfigForm(p => ({...p, payment_account_type: 'host_association'}))} disabled={!editingConfig} className="accent-cameroon-green" data-testid="account-type-host" />
                    <div>
                      <p className="font-medium text-sm">Host Association Account</p>
                      <p className="text-xs text-muted-foreground">Account belongs to this year's host association</p>
                    </div>
                  </label>
                  <label className={`flex-1 flex items-center gap-2 p-3 rounded-lg border cursor-pointer transition-colors ${configForm.payment_account_type === 'cca_owned' ? 'border-cameroon-green bg-cameroon-green/5' : 'border-muted'} ${!editingConfig ? 'opacity-60 pointer-events-none' : ''}`}>
                    <input type="radio" name="account_type" value="cca_owned" checked={configForm.payment_account_type === 'cca_owned'} onChange={() => setConfigForm(p => ({...p, payment_account_type: 'cca_owned'}))} disabled={!editingConfig} className="accent-cameroon-green" data-testid="account-type-cca" />
                    <div>
                      <p className="font-medium text-sm">CCA Owned Account</p>
                      <p className="text-xs text-muted-foreground">Account belongs to CCA Montreal directly</p>
                    </div>
                  </label>
                </div>
                <p className="text-xs text-muted-foreground">This tells clients who they are paying — the host association for this event or the CCA organization.</p>
              </div>
              <div className="grid sm:grid-cols-2 gap-3">
                <div className="space-y-2">
                  <Label>Interac e-Transfer Email</Label>
                  <Input disabled={!editingConfig} value={configForm.payment_email} onChange={e => setConfigForm(p => ({...p, payment_email: e.target.value}))} placeholder="payments@ccamontreal.com" data-testid="config-payment-email" />
                </div>
                <div className="space-y-2">
                  <Label>Interac e-Transfer Phone</Label>
                  <Input disabled={!editingConfig} value={configForm.payment_phone} onChange={e => setConfigForm(p => ({...p, payment_phone: e.target.value}))} placeholder="+1 514 000 0000" />
                </div>
              </div>
              <div className="space-y-2">
                <Label>Payment Instructions (shown to customers)</Label>
                <Textarea disabled={!editingConfig} value={configForm.payment_instructions} onChange={e => setConfigForm(p => ({...p, payment_instructions: e.target.value}))} rows={2} />
              </div>
            </CardContent>
          </Card>
          {/* Tent Types */}
          <Card>
            <CardHeader>
              <div className="flex justify-between items-center">
                <CardTitle className="text-base">Tent Types</CardTitle>
                <Button size="sm" onClick={() => setShowAddTent(!showAddTent)}><Plus className="w-4 h-4 mr-1" />Add Type</Button>
              </div>
            </CardHeader>
            <CardContent className="space-y-3">
              {showAddTent && (
                <div className="p-4 border rounded-lg space-y-3 bg-muted/50">
                  <div className="grid sm:grid-cols-2 gap-3">
                    <Input placeholder="Name (e.g. BYO Tent)" value={newTentType.name} onChange={e => setNewTentType(p => ({...p, name: e.target.value}))} />
                    <Input placeholder="Size (e.g. 10x10)" value={newTentType.size} onChange={e => setNewTentType(p => ({...p, size: e.target.value}))} />
                  </div>
                  <Input placeholder="Description" value={newTentType.description} onChange={e => setNewTentType(p => ({...p, description: e.target.value}))} />
                  <div className="grid sm:grid-cols-2 gap-3">
                    <div className="space-y-1"><Label className="text-xs">Price ($)</Label><Input type="number" step="0.01" value={newTentType.price} onChange={e => setNewTentType(p => ({...p, price: e.target.value}))} /></div>
                    <div className="space-y-1"><Label className="text-xs">Total Spots</Label><Input type="number" value={newTentType.total_spots} onChange={e => setNewTentType(p => ({...p, total_spots: e.target.value}))} /></div>
                  </div>
                  <div className="flex gap-2">
                    <Button size="sm" onClick={handleAddTentType} className="bg-cameroon-green hover:bg-cameroon-green/90">Save</Button>
                    <Button size="sm" variant="outline" onClick={() => setShowAddTent(false)}>Cancel</Button>
                  </div>
                </div>
              )}
              {config?.tent_types?.map(tt => (
                <div key={tt.id} className="flex items-center justify-between p-3 border rounded-lg" data-testid={`tent-type-row-${tt.id}`}>
                  <div>
                    <span className="font-medium">{tt.name}</span>
                    {tt.size && <Badge variant="outline" className="ml-2 text-xs">{tt.size}</Badge>}
                    <p className="text-sm text-muted-foreground">${tt.price} | {tt.reserved_spots}/{tt.total_spots} reserved</p>
                  </div>
                  <Button size="sm" variant="ghost" onClick={() => handleDeleteTentType(tt.id)}><Trash2 className="w-4 h-4 text-destructive" /></Button>
                </div>
              ))}
              {(!config?.tent_types || config.tent_types.length === 0) && !showAddTent && (
                <p className="text-muted-foreground text-sm text-center py-4">No tent types configured yet.</p>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* RESERVATIONS TAB */}
        <TabsContent value="reservations" className="space-y-3">
          {/* Status filter pills */}
          <div className="flex flex-wrap gap-2" data-testid="reservation-filters">
            <Button size="sm" variant={filterStatus === 'all' ? 'default' : 'outline'} onClick={() => setFilterStatus('all')}>All ({reservations.length})</Button>
            {Object.entries(resCounts).map(([s, count]) => (
              <Button key={s} size="sm" variant={filterStatus === s ? 'default' : 'outline'} onClick={() => setFilterStatus(s)} className="gap-1.5">
                <div className={`w-2 h-2 rounded-full ${STATUS_CONFIG[s]?.dotColor || 'bg-gray-400'}`} />
                {STATUS_CONFIG[s]?.label || s} ({count})
              </Button>
            ))}
          </div>

          {filteredReservations.length === 0 ? (
            <p className="text-center text-muted-foreground py-8">No tent reservations{filterStatus !== 'all' ? ` with status "${STATUS_CONFIG[filterStatus]?.label}"` : ''} yet.</p>
          ) : filteredReservations.map(r => (
            <Card key={r.id} data-testid={`reservation-row-${r.id}`}>
              <CardContent className="p-4">
                <div className="flex flex-wrap items-start justify-between gap-3">
                  <div className="space-y-1 flex-1 min-w-0">
                    <div className="flex flex-wrap items-center gap-2">
                      <Building2 className="w-4 h-4 text-muted-foreground" />
                      <span className="font-semibold">{r.business_name}</span>
                      <StatusBadge status={r.status} />
                      <Badge variant="outline" className="text-xs font-mono">{r.confirmation_number}</Badge>
                    </div>
                    <p className="text-sm text-muted-foreground">{r.tent_type_name} | ${r.amount?.toFixed(2)} | {r.business_type?.replace('_',' ')}{r.affiliated_association ? ` | Assoc: ${r.affiliated_association}` : ''}</p>
                    <p className="text-xs text-muted-foreground">
                      <Mail className="w-3 h-3 inline mr-1" />{r.contact_email}
                      <Phone className="w-3 h-3 inline ml-3 mr-1" />{r.contact_phone}
                    </p>
                    {r.admin_note && (
                      <div className="mt-2 p-2 rounded bg-muted/50 text-xs text-muted-foreground border-l-2 border-purple-400">
                        <strong>Admin Note:</strong> {r.admin_note}
                      </div>
                    )}
                    {r.payment_reference && (
                      <p className="text-xs text-cameroon-green mt-1"><DollarSign className="w-3 h-3 inline mr-1" />Payment Ref: {r.payment_reference}</p>
                    )}
                    {r.tent_number && (
                      <div className="mt-2 inline-flex items-center gap-2 px-3 py-1.5 rounded-lg bg-cameroon-green/10 border border-cameroon-green/20" data-testid={`tent-number-${r.id}`}>
                        <MapPin className="w-4 h-4 text-cameroon-green" />
                        <span className="text-sm font-mono font-bold text-cameroon-green">Tent #{r.tent_number}</span>
                      </div>
                    )}
                    <StatusHistory history={r.status_history} />
                  </div>
                  <div className="flex flex-col gap-2">
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() => openStatusDialog(r.id, r.status, 'reservation')}
                      data-testid={`update-reservation-${r.id}`}
                    >
                      <Send className="w-4 h-4 mr-1" />
                      Update Status
                    </Button>
                    <Button
                      size="sm"
                      variant="ghost"
                      className="text-destructive hover:text-destructive hover:bg-destructive/10"
                      onClick={() => openDeleteDialog(r.id, r.business_name, r.confirmation_number, 'reservation')}
                      data-testid={`delete-reservation-${r.id}`}
                    >
                      <Trash2 className="w-4 h-4 mr-1" />
                      Delete
                    </Button>
                  </div>
                </div>
              </CardContent>
            </Card>
          ))}
        </TabsContent>

        {/* PACKAGES TAB */}
        <TabsContent value="packages" className="space-y-3">
          <Button size="sm" onClick={() => setShowAddPkg(!showAddPkg)}><Plus className="w-4 h-4 mr-1" />Add Package</Button>
          {showAddPkg && (
            <Card>
              <CardContent className="p-4 space-y-3">
                <div className="grid sm:grid-cols-3 gap-3">
                  <Input placeholder="Package Name (e.g. Gold)" value={newPackage.name} onChange={e => setNewPackage(p => ({...p, name: e.target.value}))} />
                  <div className="space-y-1"><Label className="text-xs">Price ($)</Label><Input type="number" value={newPackage.price} onChange={e => setNewPackage(p => ({...p, price: e.target.value}))} /></div>
                  <div className="space-y-1"><Label className="text-xs">Total Available</Label><Input type="number" value={newPackage.total_available} onChange={e => setNewPackage(p => ({...p, total_available: e.target.value}))} /></div>
                </div>
                <div className="grid sm:grid-cols-2 gap-3">
                  <div className="space-y-1">
                    <Label className="text-xs">Logo Size</Label>
                    <select value={newPackage.logo_size} onChange={e => setNewPackage(p => ({...p, logo_size: e.target.value}))} className="w-full h-10 rounded-md border px-3 text-sm">
                      <option value="large">Large</option><option value="medium">Medium</option><option value="small">Small</option>
                    </select>
                  </div>
                  <div className="space-y-1"><Label className="text-xs">Display Priority (lower = higher)</Label><Input type="number" value={newPackage.display_priority} onChange={e => setNewPackage(p => ({...p, display_priority: e.target.value}))} /></div>
                </div>
                <div className="space-y-1">
                  <Label className="text-xs">Perks (one per line)</Label>
                  <Textarea value={newPackage.perks} onChange={e => setNewPackage(p => ({...p, perks: e.target.value}))} rows={3} placeholder="Logo on main stage banner&#10;Dedicated 10x20 tent&#10;MC shoutouts" />
                </div>
                <div className="flex gap-2">
                  <Button size="sm" onClick={handleAddPackage} className="bg-cameroon-yellow hover:bg-cameroon-yellow/90 text-slate-900">Create Package</Button>
                  <Button size="sm" variant="outline" onClick={() => setShowAddPkg(false)}>Cancel</Button>
                </div>
              </CardContent>
            </Card>
          )}
          {packages.map(pkg => (
            <Card key={pkg.id} data-testid={`package-row-${pkg.id}`}>
              <CardContent className="p-4">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 rounded-lg bg-cameroon-yellow/20 flex items-center justify-center">
                      <Star className="w-5 h-5 text-cameroon-yellow" />
                    </div>
                    <div>
                      <div className="flex items-center gap-2">
                        <span className="font-semibold">{pkg.name}</span>
                        <Badge variant="outline">${pkg.price}</Badge>
                        <Badge variant="outline" className="text-xs">{pkg.logo_size} logo</Badge>
                      </div>
                      <p className="text-sm text-muted-foreground">{pkg.sponsors_count}/{pkg.total_available} sponsors | Priority: {pkg.display_priority}</p>
                      {pkg.perks?.length > 0 && (
                        <div className="flex flex-wrap gap-1 mt-1">
                          {pkg.perks.map((p, i) => <Badge key={`${pkg.id}-perk-${i}`} variant="secondary" className="text-xs">{p}</Badge>)}
                        </div>
                      )}
                    </div>
                  </div>
                  <Button size="sm" variant="ghost" onClick={() => handleDeletePackage(pkg.id)}><Trash2 className="w-4 h-4 text-destructive" /></Button>
                </div>
              </CardContent>
            </Card>
          ))}
        </TabsContent>

        {/* SPONSORS TAB */}
        <TabsContent value="sponsors" className="space-y-3">
          {/* Status filter pills */}
          <div className="flex flex-wrap gap-2" data-testid="sponsor-filters">
            <Button size="sm" variant={filterStatus === 'all' ? 'default' : 'outline'} onClick={() => setFilterStatus('all')}>All ({sponsors.length})</Button>
            {Object.entries(sposCounts).map(([s, count]) => (
              <Button key={s} size="sm" variant={filterStatus === s ? 'default' : 'outline'} onClick={() => setFilterStatus(s)} className="gap-1.5">
                <div className={`w-2 h-2 rounded-full ${STATUS_CONFIG[s]?.dotColor || 'bg-gray-400'}`} />
                {STATUS_CONFIG[s]?.label || s} ({count})
              </Button>
            ))}
          </div>

          {filteredSponsors.length === 0 ? (
            <p className="text-center text-muted-foreground py-8">No sponsorship applications{filterStatus !== 'all' ? ` with status "${STATUS_CONFIG[filterStatus]?.label}"` : ''} yet.</p>
          ) : filteredSponsors.map(s => (
            <Card key={s.id} data-testid={`sponsor-row-${s.id}`}>
              <CardContent className="p-4">
                <div className="flex flex-wrap items-start justify-between gap-3">
                  <div className="space-y-1 flex-1 min-w-0">
                    <div className="flex flex-wrap items-center gap-2">
                      {s.logo_url && <img src={s.logo_url} alt="" className="w-8 h-8 rounded object-contain bg-muted" />}
                      <span className="font-semibold">{s.company_name}</span>
                      <StatusBadge status={s.status} />
                      <Badge variant="outline" className="text-xs">{s.package_name}</Badge>
                      <Badge variant="outline" className="text-xs font-mono">{s.confirmation_number}</Badge>
                    </div>
                    <p className="text-sm text-muted-foreground">${s.amount?.toFixed(2)} | {s.contact_name}{s.affiliated_association ? ` | Assoc: ${s.affiliated_association}` : ''}</p>
                    <p className="text-xs text-muted-foreground">
                      <Mail className="w-3 h-3 inline mr-1" />{s.contact_email}
                      <Phone className="w-3 h-3 inline ml-3 mr-1" />{s.contact_phone}
                    </p>
                    {s.admin_note && (
                      <div className="mt-2 p-2 rounded bg-muted/50 text-xs text-muted-foreground border-l-2 border-purple-400">
                        <strong>Admin Note:</strong> {s.admin_note}
                      </div>
                    )}
                    {s.payment_reference && (
                      <p className="text-xs text-cameroon-green mt-1"><DollarSign className="w-3 h-3 inline mr-1" />Payment Ref: {s.payment_reference}</p>
                    )}
                    <StatusHistory history={s.status_history} />
                  </div>
                  <div className="flex flex-col gap-2">
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() => openStatusDialog(s.id, s.status, 'sponsor')}
                      data-testid={`update-sponsor-${s.id}`}
                    >
                      <Send className="w-4 h-4 mr-1" />
                      Update Status
                    </Button>
                    <Button
                      size="sm"
                      variant="ghost"
                      className="text-destructive hover:text-destructive hover:bg-destructive/10"
                      onClick={() => openDeleteDialog(s.id, s.company_name, s.confirmation_number, 'sponsor')}
                      data-testid={`delete-sponsor-${s.id}`}
                    >
                      <Trash2 className="w-4 h-4 mr-1" />
                      Delete
                    </Button>
                  </div>
                </div>
              </CardContent>
            </Card>
          ))}
        </TabsContent>
      </Tabs>

      {/* Status Update Dialog */}
      <StatusUpdateDialog
        open={statusDialog.open}
        onClose={() => setStatusDialog(prev => ({ ...prev, open: false }))}
        onSubmit={handleStatusUpdate}
        currentStatus={statusDialog.currentStatus}
        itemType={statusDialog.type === 'reservation' ? 'Reservation' : 'Sponsorship'}
      />

      {/* Delete Confirmation Dialog */}
      <DeleteConfirmDialog
        open={deleteDialog.open}
        onClose={() => setDeleteDialog(prev => ({ ...prev, open: false }))}
        onConfirm={handleDelete}
        itemName={deleteDialog.name}
        confirmationNumber={deleteDialog.confirmation}
        itemType={deleteDialog.type === 'reservation' ? 'Reservation' : 'Sponsor Application'}
      />
    </div>
  );
};

export default TentSponsorManagement;
